import WebFont from 'webfontloader';

const MARKETWATCH_FONT_FACE_ID = 'chartlos-marketwatch-fonts';

const buildMarketwatchFontFaceCss = (origin) => `
@font-face {
  font-family: "Founders Grotesk Condensed";
  src: url('${origin}/assets/css/fonts/foundersGrotesk/founders-grotesk-condensed-medium.woff2') format("woff2");
  font-weight: 500;
  font-style: normal;
  font-display: swap;
}
@font-face {
  font-family: "Founders Grotesk Condensed";
  src: url('${origin}/assets/css/fonts/foundersGrotesk/founders-grotesk-condensed-semibold.woff2') format("woff2");
  font-weight: 600;
  font-style: normal;
  font-display: swap;
}
`;

/**
 * Inject the MW @font-face block into <head> once per page. Same-origin paths
 * sidestep cross-origin CORS issues and work in every environment.
 */
function ensureMarketwatchFontsLoaded() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(MARKETWATCH_FONT_FACE_ID)) return;
  const style = document.createElement('style');
  style.id = MARKETWATCH_FONT_FACE_ID;
  style.textContent = buildMarketwatchFontFaceCss(window.location.origin);
  document.head.appendChild(style);
}

/**
 * Preload fonts depending on product. If fonts are *not* preloaded, then
 * line breaks fail (because line breaks are determined with a fallback font).
 */
export function fonts(options, complete) {
  const { product, print } = options;
  if (product === 'marketwatch') {
    ensureMarketwatchFontsLoaded();
    complete();
    return;
  }
  const info = fontsInfo(product, print);
  let interval = 0;
  if (info === true) {
    complete();
  } else {
    const status = (window.__charts_fonts_status__ =
      typeof window.__charts_fonts_status__ === 'object' ? window.__charts_fonts_status__ : {});

    const callbackComplete = () => {
      status[info.product] = 'complete';
      complete();
    };

    const checkComplete = () => {
      if (status[info.product] === 'complete') {
        clearInterval(interval);
        complete();
      }
    };

    const checkQueued = () => {
      if (!areFontsLoading()) {
        clearInterval(interval);
        if (status[info.product] === 'complete') {
          complete();
        } else {
          status[info.product] = 'loading';
          WebFont.load({
            custom: info,
            active: callbackComplete,
            inactive: callbackComplete,
          });
        }
      }
    };

    if (status[info.product] === 'loading') {
      interval = setInterval(checkComplete, 1);
    } else if (status[info.product] === 'complete') {
      complete();
    } else {
      interval = setInterval(checkQueued, 1);
    }
  }
}

function areFontsLoading() {
  const status = window.__charts_fonts_status__;
  if (status) {
    const keys = Object.keys(status);
    for (let i = 0; i < keys.length; i++) {
      if (status[keys[i]] === 'loading') {
        return true;
      }
    }
  }
  return false;
}

function fontsInfo(product, print = false) {
  if (__ALL__ || __WSJ__) {
    if (product === 'wsj') {
      return {
        product,
        families: ['Retina', 'Retina Narrow', 'Noto Sans JP'],
        urls: [
          'https://asset.wsj.net/dynamic-insets/charts/wsj-digital.css',
          'https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@300;700&display=swap',
        ],
      };
    }
  }
  if (__ALL__ || __WSJOPINION__) {
    if (product === 'wsjopinion') {
      return {
        product,
        families: ['Retina', 'Retina Narrow', 'Noto Sans JP'],
        urls: [
          'https://asset.wsj.net/dynamic-insets/charts/wsj-digital.css',
          'https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@300;700&display=swap',
        ],
      };
    }
  }
  if (__ALL__ || __DJRISKJOURNAL__) {
    if (product === 'djriskjournal') {
      return {
        product,
        families: ['Retina', 'Retina Narrow'],
        urls: ['https://asset.wsj.net/dynamic-insets/charts/wsj-digital.css'],
      };
    }
  }
  if (__ALL__ || __ENERGYNEWS__) {
    if (product === 'energynews') {
      return {
        product,
        families: ['Retina', 'Retina Narrow'],
        urls: ['https://asset.wsj.net/dynamic-insets/charts/wsj-digital.css'],
      };
    }
  }
  if (__ALL__ || __OXAN__) {
    if (product === 'oxan') {
      return {
        product,
        families: ['Retina', 'Retina Narrow'],
        urls: ['https://asset.wsj.net/dynamic-insets/charts/wsj-digital.css'],
      };
    }
  }
  if (__ALL__ || __BARRONS__) {
    if (product === 'barrons') {
      return {
        product,
        families: ['Aileron'],
        urls: ['https://www.barrons.com/fonts/Aileron.css'],
      };
    }
  }
  if (__ALL__ || __FNLONDON__) {
    if (product === 'fnlondon') {
      return {
        product,
        families: ['Theinhardt:n3,n4,n6'],
        urls: ['https://s3.amazonaws.com/fonts.wsj.net/Theinhardt.css'],
      };
    }
  }
  // MarketWatch is handled separately at the top of `fonts()` — see
  // ensureMarketwatchFontsLoaded.

  return true;
}
